import { Controller } from "@hotwired/stimulus"
import { fill, number } from "../../lib/format"

const GUESSES_PER_SECOND = 1e10
const COMMON = ["password", "passw0rd", "qwerty", "letmein", "welcome", "admin", "login", "iloveyou", "monkey",
  "dragon", "master", "football", "baseball", "sunshine", "princess", "abc", "123", "1234", "12345", "123456",
  "654321", "111111", "000000", "asdf", "zxcv", "azerty"]
const LEVELS = [
  [28, "very_weak"], [36, "weak"], [60, "fair"], [90, "strong"], [Infinity, "very_strong"]
]

// An entropy estimate with penalties for the patterns attackers try first.
export default class extends Controller {
  static targets = ["password", "reveal", "meter", "label", "entropy", "crack", "checks"]
  static values = { messages: Object }

  check() {
    const pw = this.passwordTarget.value
    const checks = {
      length: pw.length >= 12,
      lower: /[a-z]/.test(pw),
      upper: /[A-Z]/.test(pw),
      digit: /\d/.test(pw),
      symbol: /[^A-Za-z0-9]/.test(pw),
      pattern: pw.length > 0 && !hasPattern(pw)
    }
    for (const li of this.checksTarget.children) li.dataset.met = String(!!checks[li.dataset.check])

    if (!pw) {
      this.paint(0, this.messagesValue.nothing)
      this.entropyTarget.innerHTML = `0 <small>${this.bitsLabel}</small>`
      this.crackTarget.textContent = "—"
      return
    }

    const bits = entropy(pw, checks)
    const level = LEVELS.findIndex(([max]) => bits < max)
    this.paint(level + 1, this.messagesValue[LEVELS[level][1]])
    this.entropyTarget.innerHTML = `${Math.round(bits)} <small>${this.bitsLabel}</small>`
    this.crackTarget.textContent = crackTime(bits, this.messagesValue)
  }

  // The "bits" label is in the page already; keep whatever it says.
  get bitsLabel() {
    if (!this._bits) this._bits = this.entropyTarget.querySelector("small")?.textContent || "bits"
    return this._bits
  }

  paint(segments, label) {
    ;[...this.meterTarget.children].forEach((bar, i) => bar.classList.toggle("on", i < segments))
    this.labelTarget.textContent = label
    this.labelTarget.classList.toggle("warn", segments > 0 && segments <= 2)
  }

  reveal() {
    const showing = this.passwordTarget.type === "text"
    this.passwordTarget.type = showing ? "password" : "text"
    if (!this.revealTarget.dataset.show) this.revealTarget.dataset.show = this.revealTarget.textContent
    this.revealTarget.textContent = showing ? this.revealTarget.dataset.show : (this.revealTarget.dataset.hide || this.revealTarget.dataset.show)
    this.revealTarget.setAttribute("aria-pressed", String(!showing))
  }
}

function entropy(pw, checks) {
  let pool = 0
  if (checks.lower) pool += 26
  if (checks.upper) pool += 26
  if (checks.digit) pool += 10
  if (checks.symbol) pool += 33
  let bits = pw.length * Math.log2(pool || 1)
  // Each pattern found cuts the estimate: the attacker tries them first.
  if (COMMON.some(word => pw.toLowerCase().includes(word))) bits -= 20
  if (/(.)\1{2,}/.test(pw)) bits -= 8
  if (/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789|qwe|wer|ert|rty|tyu|yui|uio|iop|asd|sdf|dfg|fgh|ghj|hjk|jkl|zxc|xcv|cvb|vbn|bnm)/i.test(pw)) bits -= 8
  if (/(?:19|20)\d\d/.test(pw)) bits -= 6
  return Math.max(0, bits)
}

function hasPattern(pw) {
  const lower = pw.toLowerCase()
  return COMMON.some(word => lower.includes(word)) || /(.)\1{2,}/.test(pw) || /(?:19|20)\d\d/.test(pw)
}

function crackTime(bits, m) {
  const seconds = Math.pow(2, bits) / GUESSES_PER_SECOND / 2
  if (seconds < 1) return m.instant
  const units = [["year", 31557600], ["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]]
  for (const [name, size] of units) {
    if (seconds >= size) {
      const n = seconds / size
      if (name === "year" && n > 1e9) return m.universe
      if (name === "year" && n > 1e6) return fill(m.million_years, { count: number(n / 1e6, 0) })
      return `${number(n, n < 10 ? 1 : 0)} ${m[name]}${n >= 1.5 ? "s" : ""}`
    }
  }
  return m.instant
}
